import type { ReactNode } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { ExclamationCircle } from 'react-bootstrap-icons';
import type { DeploymentRecord } from '@/services/deploymentService';
import type { FailureInfo } from '@/utils/deployFailureStore';
import { isLive, statusKey, waitPhrase } from '@/utils/deployStatus';
import './deployments.css';

interface FailureLineProps {
  info?: FailureInfo;
  /** A newer run for the same client, when there is one. This failure is then old news. */
  newerRun?: DeploymentRecord;
  onRunAgain?: () => void;
  /** Why "Run again" is off, when it is. */
  runAgainBlocked?: string;
  /** Say why it failed, and nothing about a second run: it is not yet known whether a later run exists. */
  hideOffer?: boolean;
}

/**
 * The one line under a failed run that says why it failed: a short title, then
 * the real error. Where a second run is likely to fix the error, it says so and
 * offers it. "Likely", because the verdict comes from the error's text.
 */
export function FailureLine({ info, newerRun, onRunAgain, runAgainBlocked, hideOffer = false }: FailureLineProps) {
  if (!info || info.state === 'loading') {
    return (
      <div className="deploy-reason deploy-reason--quiet">
        <Spinner animation="border" size="sm" className="deploy-reason__spinner" />
        <span>Reading the log for the error</span>
      </div>
    );
  }

  const { failure } = info;
  if (!failure) {
    return (
      <div className="deploy-reason deploy-reason--quiet">{info.note || 'No reason was recorded for this run.'}</div>
    );
  }

  const full = [failure.title, failure.summary, info.note].filter(Boolean).join('\n');
  let outcome: ReactNode = null;
  if (info.fromFirstAttempt) {
    // The pipeline already ran it a second time, so offering a second run here would mislead.
    outcome = <span className="deploy-reason__later">The second attempt failed and gave no reason</span>;
  } else if (newerRun) {
    const later = statusKey(newerRun);
    outcome = (
      <span className="deploy-reason__later">
        {isLive(newerRun)
          ? 'Running again now'
          : later === 'success'
            ? 'A later run succeeded'
            : later === 'failed'
              ? 'A later run failed too'
              : 'A later run replaced this one'}
      </span>
    );
  } else if (failure.retry !== 'never' && !hideOffer) {
    outcome = (
      <>
        <span className="deploy-reason__fix">A second run likely fixes this{waitPhrase(failure.waitSeconds)}</span>
        {onRunAgain && (
          <Button
            size="sm"
            variant="outline-secondary"
            className="deploy-reason__button"
            onClick={onRunAgain}
            disabled={Boolean(runAgainBlocked)}
            title={runAgainBlocked}
          >
            Run again
          </Button>
        )}
      </>
    );
  }

  return (
    <div className="deploy-reason">
      <ExclamationCircle className="deploy-reason__icon" aria-hidden="true" />
      {info.fromFirstAttempt && <span className="deploy-reason__more">First attempt:</span>}
      <span className="deploy-reason__title">{failure.title}</span>
      {(failure.summary || info.note) && (
        <span className="deploy-reason__text" title={full}>
          {failure.summary || info.note}
        </span>
      )}
      {info.blocks.length > 1 && (
        <span
          className="deploy-reason__more"
          title={`The log holds ${info.blocks.length} errors. Open the details to read them all.`}
        >
          +{info.blocks.length - 1} more
        </span>
      )}
      {outcome}
    </div>
  );
}

export default FailureLine;
